/* ==========================================================================
   CANVAS PARTICLE BACKDROP ENGINE (ts/canvas-backdrop.ts)
   ========================================================================== */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
}

export function initCanvasBackdrop(containerId: string): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  const canvas = document.createElement('canvas');
  canvas.style.position = 'absolute';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '0';
  container.style.position = 'relative';
  container.insertBefore(canvas, container.firstChild);

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = container.offsetWidth;
  canvas.height = container.offsetHeight;
  let width = canvas.width;
  let height = canvas.height;

  const particles: Particle[] = [];
  const particleCount = Math.min(Math.floor((width * height) / 12000), 75);

  let mouseX = width / 2;
  let mouseY = height / 2;
  let targetMouseX = mouseX;
  let targetMouseY = mouseY;

  for (let i = 0; i < particleCount; i++) {
    particles.push({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      radius: Math.random() * 2 + 1,
      alpha: Math.random() * 0.5 + 0.2,
    });
  }

  const handleResize = () => {
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;
    width = canvas.width;
    height = canvas.height;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const rect = container.getBoundingClientRect();
    targetMouseX = e.clientX - rect.left;
    targetMouseY = e.clientY - rect.top;
  };

  window.addEventListener('resize', handleResize);
  container.addEventListener('mousemove', handleMouseMove);

  function render() {
    ctx?.clearRect(0, 0, width, height);

    // Smooth mouse interpolation
    mouseX += (targetMouseX - mouseX) * 0.05;
    mouseY += (targetMouseY - mouseY) * 0.05;

    // Draw ambient cursor glow gradient
    if (ctx) {
      const gradient = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 350);
      gradient.addColorStop(0, 'rgba(157, 255, 0, 0.08)');
      gradient.addColorStop(0.5, 'rgba(56, 189, 248, 0.03)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Render & update particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        const dx = mouseX - p.x;
        const dy = mouseY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius + (dist < 150 ? (150 - dist) / 50 : 0), 0, Math.PI * 2);
        ctx.fillStyle =
          dist < 150 ? `rgba(157, 255, 0, ${p.alpha + 0.3})` : `rgba(255, 255, 255, ${p.alpha * 0.6})`;
        ctx.fill();
      });
    }

    requestAnimationFrame(render);
  }

  render();
}
